/**
 * The exponential distribution with rate λ (mean 1/λ), in the form R's `dexp`, `pexp`
 * and `qexp` use. Every function takes the rate; the mean-parameterised textbooks
 * (scale β = 1/λ) convert once, with `rateFromMean`.
 *
 * The CDF uses `expm1` so tiny x does not lose its digits to 1 − e^(−λx), and the
 * survival function is the direct exponential so deep tails stay exact.
 */

export function rateFromMean(mean: number): number {
  return 1 / mean;
}

export function exponentialPdf(x: number, rate: number): number {
  return x < 0 ? 0 : rate * Math.exp(-rate * x);
}

/** P(X ≤ x). */
export function exponentialCdf(x: number, rate: number): number {
  return x <= 0 ? 0 : -Math.expm1(-rate * x);
}

/** P(X > x), the survival (reliability) function. */
export function exponentialSurvival(x: number, rate: number): number {
  return x <= 0 ? 1 : Math.exp(-rate * x);
}

/** P(a ≤ X ≤ b), the two bounds put in order. */
export function exponentialBetween(a: number, b: number, rate: number): number {
  const low = Math.min(a, b);
  const high = Math.max(a, b);
  // Subtract in the tail that keeps digits: the survival values when both are far out.
  if (rate * low > 0.5) {
    return Math.max(
      0,
      exponentialSurvival(low, rate) - exponentialSurvival(high, rate)
    );
  }
  return Math.max(0, exponentialCdf(high, rate) - exponentialCdf(low, rate));
}

/** The x with P(X ≤ x) = p, for 0 ≤ p < 1. */
export function exponentialQuantile(p: number, rate: number): number {
  return -Math.log1p(-p) / rate;
}

export function exponentialMoments(rate: number) {
  return {
    mean: 1 / rate,
    variance: 1 / (rate * rate),
    sd: 1 / rate,
    median: Math.LN2 / rate,
    /** The hazard rate f(x) / S(x) is the same λ at every x. */
    hazard: rate,
  };
}

export interface MemorylessCheck {
  /** P(X > s + t). */
  numerator: number;
  /** P(X > s). */
  denominator: number;
  /** P(X > s + t | X > s). */
  conditional: number;
  /** P(X > t), which the conditional probability always equals. */
  unconditional: number;
}

/**
 * P(X > s + t | X > s) = S(s + t) / S(s), alongside P(X > t). They are equal: having
 * already waited s tells you nothing about the wait still to come.
 */
export function memorylessCheck(
  s: number,
  t: number,
  rate: number
): MemorylessCheck {
  const numerator = exponentialSurvival(s + t, rate);
  const denominator = exponentialSurvival(s, rate);
  return {
    numerator,
    denominator,
    conditional: denominator > 0 ? numerator / denominator : 0,
    unconditional: exponentialSurvival(t, rate),
  };
}
